import React from 'react';
import { useUID } from 'react-uid';
import InputBase, { InputBaseProps } from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import FormLabel from '@mui/material/FormLabel';
import { alpha } from '@mui/system';
import Box, { BoxProps } from '@mui/system/Box';
import { styled, useThemeProps } from '@mui/material/styles';
import { shouldForwardProp } from '../../util/shouldForwardProps';

const useUtilityClasses = (props: InputProps) => {
  const { error, classes = {} } = props;
  const slots = {
    root: ['root', error && 'error'],
    label: ['label', error && 'error'],
    input: ['input', error && 'error'],
    helperText: ['helperText', error && 'error'],
  };

  const composedClasses: Record<string, string> = {};
  Object.keys(slots).forEach((key) => {
    const slotClasses = slots[key as keyof typeof slots];
    composedClasses[key] = slotClasses
      .filter(Boolean)
      .map((cls) => (typeof cls === 'string' ? `FSInputV2-${cls}` : ''))
      .join(' ');
  });

  return {
    ...classes,
    ...composedClasses,
  };
};

interface OwnInputProps {
  label?: string | null;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  slots?: { label?: React.ElementType };
  slotProps?: { label?: any };
  classes?: Record<string, string>;
  sx?: BoxProps['sx'];
  sxInput?: BoxProps['sx'];
  ownerState?: any;
  required?: boolean;
  id?: string;
  [key: string]: any;
}

export type InputProps = OwnInputProps & Omit<InputBaseProps, 'classes'>;

const InputV2 = React.forwardRef(function CustomInputV2(
  inProps: InputProps,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const props = useThemeProps({ props: inProps, name: 'FSInputV2' });
  const uniqueId = useUID();
  const id = props.id || `FSInputV2-${uniqueId}`;
  const name = props.name || id;
  const { label: Label = FormLabel } = props.slots || {};
  const classes = useUtilityClasses(props);

  const {
    label,
    error,
    helperText,
    fullWidth,
    slots,
    slotProps,
    sx,
    sxInput,
    ownerState,
    required,
    ...inputBaseProps
  } = props;

  return (
    <StyledContainerRoot
      className={classes.root}
      sx={sx as any}
      fullWidth={fullWidth}
    >
      {label && (
        <Label htmlFor={id} className={classes.label} required={required}>
          {label}
        </Label>
      )}
      <StyledInputBase
        id={id}
        name={name}
        ref={ref}
        className={classes.input}
        error={error}
        fullWidth={fullWidth}
        ownerState={{ ...ownerState, value: props.value }}
        sx={sxInput}
        {...inputBaseProps}
      />
      {helperText && (
        <Typography
          color={error ? 'error' : undefined}
          sx={{ lineHeight: '2rem' }}
          className={classes.helperText}
        >
          {helperText}
        </Typography>
      )}
    </StyledContainerRoot>
  );
});

export default InputV2;

const StyledContainerRoot = styled(Box, {
  shouldForwardProp,
})<InputProps>(({ theme, ...props }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  ...(props.fullWidth && {
    width: '100%',
  }),
}));

const StyledInputBase = styled(InputBase, {
  shouldForwardProp,
})<InputBaseProps & { ownerState?: any }>(({ theme, ...props }) => {
  const styles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '3px solid transparent',
    fontWeight: 800,
    fontFamily: 'inherit',
    fontSize: '1rem',
    lineHeight: 1,
    padding: '6px',
    borderRadius: '12px',
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[200]
        : theme.palette.grey[900],
    background: 'transparent',
    borderWidth: theme.palette.mode === 'dark' ? '3px' : '2px',
    borderStyle: 'solid',
    borderColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[200] : '#C6C6C6',
    '& input': {
      padding: 0,
    },
    '& input::placeholder': {
      color:
        theme.palette.mode === 'dark' ? theme.palette.grey[600] : '#C6C6C6',
    },

    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active':
      {
        WebkitBoxShadow: `0 0 0 30px ${
          theme.palette.mode === 'dark'
            ? theme.palette.grey[200]
            : 'rgb(238, 238, 238)'
        } inset !important`,
        WebkitTextFillColor: `${theme.palette.grey[900]} !important`,
      },

    '&:hover': {
      borderColor: alpha(
        theme.palette.primary.main,
        1 - theme.palette.action.hoverOpacity
      ),
    },

    '&:focus, &.Mui-focused': {
      borderColor: `${theme.palette.primary.main}!important`,
    },

    ...(props.ownerState?.value &&
      theme.palette.mode === 'dark' && {
        background: theme.palette.grey[200],
        color: theme.palette.grey[900],
        '& svg': {
          color: theme.palette.grey[900],
        },
      }),

    ...(props.disabled && {
      borderColor: '#9E9E9E!important',
      color: theme.palette.grey[300],
      WebkitTextFillColor: theme.palette.grey[300],
      background: '#9E9E9E',
    }),

    ...(props.error && {
      borderColor: theme.palette.error.main,
    }),

    ...(props.fullWidth && {
      width: '100%',
    }),
  };

  return styles;
});
